import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckSquare, FolderOpen, Wallet, Package, MessageSquare, CalendarDays,
  ClipboardCheck, Route, Briefcase, FileText, Newspaper, GraduationCap,
  ChevronRight, Clock, Zap, Bot, BookOpen, Smartphone,
} from "lucide-react";

const PERSONAL_SHORTCUTS = [
  { to: "/mon-espace/taches", icon: CheckSquare, label: "Mes Tâches", desc: "To-do personnelle et pro", color: "#f7c935", badgeKey: "tasks" },
  { to: "/mon-espace/documents", icon: FolderOpen, label: "Mes Documents", desc: "Vos fichiers personnels", color: "#38bdf8" },
  { to: "/mon-espace/depenses", icon: Wallet, label: "Mes Dépenses", desc: "Notes de frais", color: "#10b981" },
  { to: "/mon-espace/equipement", icon: Package, label: "Mon Équipement", desc: "Matériel attribué", color: "#a855f7" },
  { to: "/mon-espace/bulletins", icon: FileText, label: "Mes Bulletins", desc: "Bulletins de paie", color: "#22c55e" },
  { to: "/messages", icon: MessageSquare, label: "Messages", desc: "Votre messagerie", color: "#f472b6", badgeKey: "messages" },
  { to: "/agenda", icon: CalendarDays, label: "Agenda", desc: "Rendez-vous et échéances", color: "#fb923c" },
];

const PRESTATIONS = [
  { to: "/audits", icon: ClipboardCheck, label: "Audits", key: "audits" },
  { to: "/accompagnement", icon: Route, label: "Accompagnement", key: "accompagnement" },
  { to: "/services", icon: Briefcase, label: "Services", key: "services" },
  { to: "/documents", icon: FileText, label: "Documents", key: "documents" },
  { to: "/club", icon: Newspaper, label: "Club", key: "club" },
  { to: "/formations", icon: GraduationCap, label: "Formations", key: "formations" },
  { to: "/admin/agent-ia", icon: Bot, label: "Expert Stratégie", key: "expert_strategie" },
];

export default function MonEspaceHomePage() {
  const { user, axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [access, setAccess] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/personal/tasks"),
      api.get("/notifications/counts"),
      api.get("/client/my-access"),
    ]).then(([t, c, a]) => {
      if (t.status === "fulfilled" && Array.isArray(t.value.data)) setTasks(t.value.data);
      if (c.status === "fulfilled") setCounts(c.value.data || {});
      if (a.status === "fulfilled") setAccess(a.value.data || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTasks = tasks.filter((t) => t.status !== "done");
  const badges = { tasks: openTasks.length, messages: counts.messages || 0 };
  const prestations = PRESTATIONS.filter((p) => access?.[p.key]);
  const firstName = user?.first_name || user?.name?.split(" ")[0] || "";

  // Iter 276 — raccourcis conditionnels selon les rôles
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const shortcuts = [...PERSONAL_SHORTCUTS, { to: "/salarie", icon: Smartphone, label: "App Blitzz Salarié", desc: "Version mobile & hors ligne, notifications push", color: "#f7c935" }];
  if (user?.role === "tenant_member") {
    shortcuts.push({ to: "/mon-espace/pointages", icon: Clock, label: "Mes Pointages", desc: "Pointer et consulter mes heures", color: "#22c55e" });
  }
  if (roles.includes("formateur") || user?.role === "formateur") {
    shortcuts.push({ to: "/formateur/sessions", icon: GraduationCap, label: "Espace Formateur", desc: "Mes sessions à animer", color: "#a855f7" });
  }
  if (roles.includes("stagiaire") || roles.includes("eleve")) {
    shortcuts.push({ to: "/mes-formations", icon: BookOpen, label: "Espace Stagiaire", desc: "Mes formations en cours", color: "#38bdf8" });
  }

  // Élève pur : son espace est /mes-formations (Q2a)
  if (user?.role === "eleve" || user?.role === "stagiaire") {
    return <Navigate to="/mes-formations" replace />;
  }

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="mon-espace-home">
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "#10b981" }}>
          <Zap className="w-3.5 h-3.5" /> Mon Espace
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-white" data-testid="espace-home-greeting">
          Bonjour {firstName} 👋
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Votre espace personnel : retrouvez ici vos outils, vos prestations et vos documents.
        </p>
      </div>

      {/* Raccourcis espace personnel */}
      <div className="mb-8">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[#a899b8] mb-3">Mon espace personnel</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shortcuts.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="group bg-[#14101c] border border-white/[0.06] rounded-xl p-4 flex items-center gap-3.5 hover:border-white/20 transition-colors"
              data-testid={`espace-shortcut${s.to.replace(/\//g, "-")}`}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  {s.label}
                  {s.badgeKey && badges[s.badgeKey] > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white">
                      {badges[s.badgeKey] > 99 ? "99+" : badges[s.badgeKey]}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{s.desc}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors flex-shrink-0" />
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prochaines tâches */}
        <div className="bg-[#14101c] border border-white/[0.06] rounded-xl p-5" data-testid="espace-next-tasks">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: "#f7c935" }} /> Prochaines tâches
            </h2>
            <Link to="/mon-espace/taches" className="text-xs font-semibold hover:underline" style={{ color: "#f7c935" }} data-testid="espace-all-tasks-link">
              Tout voir →
            </Link>
          </div>
          {openTasks.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">Aucune tâche en cours. Profitez-en 🎉</p>
          ) : (
            <div className="space-y-2">
              {openTasks.slice(0, 4).map((t) => (
                <Link key={t.task_id} to="/mon-espace/taches" className="flex items-center gap-3 p-2.5 rounded-lg bg-[#0b0811] border border-white/[0.04] hover:border-white/15 transition-colors">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.priority === "high" ? "bg-red-400" : t.priority === "low" ? "bg-slate-500" : "bg-[#f7c935]"}`} />
                  <span className="text-sm text-slate-300 flex-1 truncate">{t.title}</span>
                  {t.due_date && (
                    <span className="text-[10px] text-slate-500 flex-shrink-0">
                      {new Date(t.due_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Prestations Blitzz */}
        <div className="bg-[#14101c] border border-white/[0.06] rounded-xl p-5" data-testid="espace-prestations">
          <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4" style={{ color: "#f7c935" }} /> Mes prestations
          </h2>
          {prestations.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">Aucune prestation active pour le moment.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {prestations.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="flex items-center gap-2.5 p-3 rounded-lg bg-[#0b0811] border border-white/[0.04] hover:border-[#f7c935]/30 transition-colors"
                  data-testid={`espace-prestation-${p.key}`}
                >
                  <p.icon className="w-4 h-4 flex-shrink-0" style={{ color: "#f7c935" }} />
                  <span className="text-sm text-slate-300 truncate">{p.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
